import { METRICS_TABS, MetricsTabId } from './constants';

export type MetricsNotificationSeverity = 'critical' | 'warning' | 'info';

export interface MetricsNotificationHistory {
  endedAt: number | null;
  durationMinutes: number | null;
}

export interface MetricsNotification {
  id: string;
  tabId: MetricsTabId;
  title: string;
  message: string;
  severity: MetricsNotificationSeverity;
  timestamp: number;
  history?: MetricsNotificationHistory;
}

export const NOTIFICATION_SEVERITY: Record<
  MetricsNotificationSeverity,
  { label: string; color: string }
> = {
  critical: { label: 'Critical', color: '#D92D20' },
  warning: { label: 'Warning', color: '#E8A317' },
  info: { label: 'Info', color: '#4C9AE8' },
};

export const tabLabel = (tabId: MetricsTabId) =>
  METRICS_TABS.find((tab) => tab.id === tabId)?.label || tabId;

export const activeAlertLabel = (count: number) => {
  if (count === 0) {
    return 'No active alerts';
  }

  if (count === 1) {
    return '1 active alert';
  }

  return `${count} active alerts`;
};

export const alertWindowLabel = (count: number, hours: number) => {
  if (count === 0) {
    return `No historical alerts in the last ${hours} hours`;
  }

  if (count === 1) {
    return `1 historical alert in the last ${hours} hours, including cleared`;
  }

  return `${count} historical alerts in the last ${hours} hours, including cleared`;
};

export const formatDurationMinutes = (minutes: number | null | undefined) => {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) {
    return null;
  }

  const rounded = Math.round(minutes);
  if (rounded < 60) {
    return `${rounded}m`;
  }

  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
};

export const historyStatusLabel = (history: MetricsNotificationHistory) => {
  const duration = formatDurationMinutes(history.durationMinutes);
  const state = history.endedAt == null ? 'Active' : 'Cleared';
  return duration ? `${state} · ${duration}` : state;
};
